import { Box, Item, Link, Text } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  BreakConfigurationFragment,
  TimePlanningPlannedBreakBreakTypeEnum as BreakType,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import DropdownSearchBar from 'modules/core/components/DropdownSearchBar'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import TooltipIcon from 'modules/shift_management/components/ShiftManagement/TooltipIcon'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import { useGetBreakConfigurations } from 'modules/time_categories_settings/hooks/breakConfigurations/useGetBreakConfigurations'

import PlannedBreaks from './PlannedBreaks'

interface Props {
  plannedBreakForms: PlannedBreakForm[]
  onAddBreak: (breakConfiguration: BreakConfigurationFragment, breakType: BreakType) => void
  onDeleteBreak: (breakForm: PlannedBreakForm) => void
  newVersion?: boolean
}

const PlannedBreaksSelector = ({
  plannedBreakForms,
  onAddBreak,
  onDeleteBreak,
  newVersion = false,
}: Props) => {
  const [{ canManageBreakConfigurations }] = usePermissions()
  const { loading, breakConfigurations } = useGetBreakConfigurations({ active: true })
  const { navigation } = useNavigation()
  const [hasSeenAddBreaksCallout, setHasSeenAddBreaksCallout] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.shiftManagementHideAddBreaksCallout,
    false
  )

  const breakSettingsLink = () => {
    return canManageBreakConfigurations ? (
      <Link.Router to={navigation.settings.time_categories_settings.fullPath()} type="solid">
        {i18n.t('shift_management.actions_sidepanel.breaks.callout.button')}
      </Link.Router>
    ) : undefined
  }

  const handleAddBreak = (option: Item) => {
    const breakConfig = breakConfigurations.find((bc) => bc.id === option.value)
    if (!breakConfig) return
    onAddBreak(breakConfig, 'flexible')
  }

  if (loading) return <></>

  const breakConfigurationsOptions = breakConfigurations.map((breakConfiguration) => ({
    label: breakConfiguration.name,
    value: breakConfiguration.id,
  }))

  return (
    <Box gap="s16">
      <Box flexDirection="row" gap="s16">
        <TooltipIcon.Medium
          icon="coffee"
          label={i18n.t('shift_management.actions_sidepanel.tooltip.breaks')}
          color="grey700"
        />
        <Box gap="s16" width="full">
          <DropdownSearchBar
            popoverWidth="s300"
            searchLabel={i18n.t('shift_management.actions_sidepanel.breaks.search')}
            sortLabel={i18n.t('common.name')}
            labelNotFound={i18n.t('shift_management.actions_sidepanel.breaks.not_found')}
            data={breakConfigurationsOptions}
            onSelect={handleAddBreak}
            // Only required when the children is the default selector.
            hideLabel
            label={i18n.t('shift_management.actions_sidepanel.breaks.search')}
            name=""
          >
            <Box alignItems="flexStart" height="s24" justifyContent="center">
              <Text.Normal size="200" color="grey1000" weight="medium">
                {i18n.t('shift_management.actions_sidepanel.add_breaks')}
              </Text.Normal>
            </Box>
          </DropdownSearchBar>
          {!!plannedBreakForms.length && (
            <PlannedBreaks
              plannedBreakForms={plannedBreakForms}
              onDelete={onDeleteBreak}
              newVersion={newVersion}
            />
          )}

          {!breakConfigurations.length && !hasSeenAddBreaksCallout && (
            <Callout
              type="info"
              title={i18n.t('shift_management.actions_sidepanel.breaks.callout.title')}
              description={i18n.t('shift_management.actions_sidepanel.breaks.callout.description')}
              link={breakSettingsLink()}
              onRemove={() => setHasSeenAddBreaksCallout(true)}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default observer(PlannedBreaksSelector)
